import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ShoppingCart,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Trash,
  Spinner,
  Package,
  Bell,
  Warning,
  ArrowRight
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATUS_COLORS = {
  pending_conversion: 'bg-amber-100 text-amber-800',
  converted_to_po: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  returned: 'bg-orange-100 text-orange-700'
};

const STATUS_LABELS = {
  pending_conversion: 'Pending Review',
  converted_to_po: 'Converted to PO',
  declined: 'Declined',
  returned: 'Returned — Action Required'
};

const PO_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  pending_dh_approval: 'bg-amber-100 text-amber-700',
  pending_fc_approval: 'bg-amber-100 text-amber-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  po_issued: 'bg-blue-100 text-blue-700',
  vendor_confirmed: 'bg-teal-100 text-teal-700',
  documents_uploaded: 'bg-orange-100 text-orange-700',
  in_transit: 'bg-emerald-100 text-emerald-700',
  delivered: 'bg-teal-100 text-teal-700',
  goods_received: 'bg-green-100 text-green-700',
  closed: 'bg-slate-700 text-white',
  rejected: 'bg-red-100 text-red-700',
};

const PO_STATUS_LABELS = {
  draft: 'Draft',
  pending_dh_approval: 'Awaiting DH Approval',
  pending_fc_approval: 'Awaiting FC Approval',
  pending_approval: 'Awaiting Approval',
  po_issued: 'PO Issued',
  vendor_confirmed: 'Vendor Confirmed',
  documents_uploaded: 'Documents Uploaded',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  goods_received: 'Goods Received',
  closed: 'Closed',
  rejected: 'Rejected',
};

const CURRENCY_SYMBOLS = { USD: '$', XCD: 'EC$', JMD: 'J$', TTD: 'TT$' };

const URGENCY_COLORS = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700'
};

const BuyerPortalPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    department_name: user?.department || '',
    budget_code: '',
    urgency_level: 'medium',
    notes: '',
    items: [{ item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0 }]
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    department_name: '',
    budget_code: '',
    urgency_level: 'medium',
    notes: '',
    items: [{ item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0 }]
  });

  useEffect(() => {
    fetchRequisitions();
    fetchNotifications();
    fetchPurchaseOrders();
    // Set department from user profile
    if (user?.department) {
      setFormData(prev => ({ ...prev, department_name: user.department }));
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/buyer-portal/notifications`, getAuthHeaders());
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/buyer-portal/notifications/${notificationId}/read`, {}, getAuthHeaders());
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchRequisitions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/buyer-portal/my-requisitions`, getAuthHeaders());
      setRequisitions(res.data);
    } catch (error) {
      toast.error('Failed to load requisitions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/buyer-portal/my-purchase-orders`, getAuthHeaders());
      setPurchaseOrders(res.data || []);
    } catch (error) {
      // Non-blocking — Buyer Portal still works without PO list.
      console.error('Failed to load purchase orders');
    }
  };

  const handleAddItem = () => {
    if (formData.items.length >= 20) {
      toast.error('Maximum 20 items per requisition');
      return;
    }
    setFormData({
      ...formData,
      items: [...formData.items, { item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateEstimatedTotal = () => {
    return formData.items.reduce((total, item) => {
      return total + (item.quantity * item.estimated_unit_price);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.department_name) {
      toast.error('Department is required');
      return;
    }
    if (formData.items.some(item => !item.item_name)) {
      toast.error('All items must have a name');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/buyer-portal/requisitions`, formData, getAuthHeaders());
      toast.success('Requisition submitted successfully!');
      setCreateDialogOpen(false);
      // Reset form
      setFormData({
        department_name: user?.department || '',
        budget_code: '',
        urgency_level: 'medium',
        notes: '',
        items: [{ item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0 }]
      });
      fetchRequisitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit requisition');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (req) => {
    setEditingReq(req);
    setEditFormData({
      department_name: req.department_name || user?.department || '',
      budget_code: req.budget_code || '',
      urgency_level: req.urgency_level || 'medium',
      notes: req.notes || '',
      items: req.items?.map(item => ({
        item_name: item.item_name || '',
        quantity: item.quantity || 1,
        unit: item.unit || item.unit_of_measure || 'units',
        estimated_unit_price: item.estimated_unit_price || 0
      })) || [{ item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0 }]
    });
    setEditDialogOpen(true);
  };

  const handleEditItemChange = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index][field] = value;
    setEditFormData({ ...editFormData, items: newItems });
  };

  const handleAddEditItem = () => {
    if (editFormData.items.length >= 20) {
      toast.error('Maximum 20 items per requisition');
      return;
    }
    setEditFormData({
      ...editFormData,
      items: [...editFormData.items, { item_name: '', quantity: 1, unit: 'units', estimated_unit_price: 0 }]
    });
  };

  const handleRemoveEditItem = (index) => {
    if (editFormData.items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = editFormData.items.filter((_, i) => i !== index);
    setEditFormData({ ...editFormData, items: newItems });
  };

  const calculateEditTotal = () => {
    return editFormData.items.reduce((total, item) => {
      return total + (item.quantity * item.estimated_unit_price);
    }, 0);
  };

  const handleResubmit = async (e) => {
    e.preventDefault();
    if (!editingReq) return;
    
    if (!editFormData.department_name) {
      toast.error('Department is required');
      return;
    }
    if (editFormData.items.some(item => !item.item_name)) {
      toast.error('All items must have a name');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/buyer-portal/requisitions/${editingReq.id}/resubmit`,
        editFormData,
        getAuthHeaders()
      );
      toast.success('Requisition edited and resubmitted!');
      setEditDialogOpen(false);
      setEditingReq(null);
      fetchRequisitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit requisition');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#F59E0B] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
            <ShoppingCart size={24} className="text-[#F59E0B]" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Buyer Portal
            </h1>
            <p className="text-slate-500">Welcome, {user?.full_name}</p>
          </div>
        </div>
        <Button 
          onClick={() => setCreateDialogOpen(true)}
          className="bg-[#F59E0B] hover:bg-[#D97706]"
          data-testid="new-requisition-btn"
        >
          <Plus size={18} className="mr-2" />
          New Requisition
        </Button>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase font-medium">Pending</p>
          <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="status-pending">
            {requisitions.filter(r => r.status === 'pending_conversion').length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-orange-200 p-4 shadow-sm">
          <p className="text-xs text-orange-600 uppercase font-medium">Returned</p>
          <p className="text-2xl font-bold text-orange-600 mt-1" data-testid="status-returned">
            {requisitions.filter(r => r.status === 'returned').length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase font-medium">Approved</p>
          <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="status-approved">
            {requisitions.filter(r => r.status === 'approved').length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase font-medium">Converted to PO</p>
          <p className="text-2xl font-bold text-green-600 mt-1" data-testid="status-converted">
            {requisitions.filter(r => r.status === 'converted_to_po').length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase font-medium">Declined</p>
          <p className="text-2xl font-bold text-red-600 mt-1" data-testid="status-declined">
            {requisitions.filter(r => r.status === 'declined').length}
          </p>
        </div>
      </div>

      {/* My Purchase Orders (View-Only) — POs originating from this buyer's PRs */}
      {purchaseOrders.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="buyer-pos-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Package size={20} className="text-[#0F52BA]" />
                My Purchase Orders
              </h2>
              <p className="text-sm text-slate-500">
                {purchaseOrders.length} {purchaseOrders.length === 1 ? 'PO' : 'POs'} created from your requisitions
                <span className="ml-2 text-xs text-slate-400">(view only)</span>
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">PO Number</th>
                  <th className="px-4 py-3 text-left">Vendor</th>
                  <th className="px-4 py-3 text-left">Items</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3 text-left">Required By</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {purchaseOrders.map(po => {
                  const itemsSummary = (po.line_items || [])
                    .slice(0, 2)
                    .map(li => li.item_name)
                    .filter(Boolean)
                    .join(', ');
                  const extra = (po.line_items?.length || 0) - 2;
                  const symbol = CURRENCY_SYMBOLS[po.currency] || po.currency || '$';
                  return (
                    <tr key={po.id} className="hover:bg-slate-50" data-testid={`buyer-po-row-${po.po_number}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{po.po_number}</td>
                      <td className="px-4 py-3 text-slate-700">{po.vendor_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">
                        {itemsSummary || '—'}{extra > 0 && <span className="text-slate-400"> +{extra} more</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-800">
                        {symbol}{(po.total_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {po.required_delivery_date ? format(new Date(po.required_delivery_date), 'MMM d, yyyy') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${PO_STATUS_COLORS[po.status] || 'bg-slate-100 text-slate-600'}`}>
                          {PO_STATUS_LABELS[po.status] || po.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* My Requisitions */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">My Purchase Requisitions</h2>
          <p className="text-sm text-slate-500">{requisitions.length} requisitions</p>
        </div>

        {requisitions.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <FileText size={48} className="mx-auto mb-4 text-slate-300" />
            <p>No requisitions submitted yet</p>
            <p className="text-sm mt-2">Click "New Requisition" to submit your first purchase request</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Items</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Est. Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Urgency</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Details / Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requisitions.map(req => (
                  <tr key={req.id} className={`hover:bg-slate-50/50 ${req.status === 'returned' ? 'bg-orange-50/40' : ''}`} data-testid={`buyer-req-row-${req.id}`}>
                    <td className="px-4 py-4 font-medium text-slate-800">{req.department_name}</td>
                    <td className="px-4 py-4 text-slate-600 max-w-[200px]">
                      {req.items?.map(i => i.item_name).join(', ').substring(0, 40)}...
                    </td>
                    <td className="px-4 py-4 font-medium text-slate-800">
                      ${req.items?.reduce((t, i) => t + (i.quantity * (i.estimated_unit_price || 0)), 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${URGENCY_COLORS[req.urgency_level]}`}>
                        {req.urgency_level?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[req.status]}`} data-testid={`buyer-req-status-${req.id}`}>
                        {STATUS_LABELS[req.status] || req.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {req.created_at ? format(new Date(req.created_at), 'MMM d, yyyy') : '-'}
                    </td>
                    <td className="px-4 py-4">
                      {req.status === 'returned' ? (
                        <div className="space-y-2">
                          <div className="bg-orange-50 border border-orange-200 rounded p-2" data-testid={`buyer-return-reason-${req.id}`}>
                            <p className="text-xs font-semibold text-orange-700">PM Note:</p>
                            <p className="text-sm text-orange-600">{req.return_reason}</p>
                            {req.returned_by_name && (
                              <p className="text-xs text-orange-500 mt-1">— {req.returned_by_name}</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => openEditDialog(req)}
                            className="bg-orange-600 hover:bg-orange-700 w-full"
                            data-testid={`buyer-edit-resubmit-btn-${req.id}`}
                          >
                            Edit & Resubmit
                          </Button>
                        </div>
                      ) : req.is_split && req.split_pos?.length > 0 ? (
                        <div className="space-y-1" data-testid={`buyer-split-pos-${req.id}`}>
                          <p className="text-xs font-medium text-purple-600 mb-1">Split into {req.split_pos.length} POs:</p>
                          {req.split_pos.map((po, i) => (
                            <div key={i} className="bg-purple-50 rounded p-1.5 text-xs">
                              <div className="flex justify-between">
                                <span className="font-medium text-purple-700">{po.po_number}</span>
                                <span className={`px-1.5 py-0.5 rounded-full text-xs ${po.status === 'po_issued' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {po.status?.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <p className="text-slate-600">{po.vendor_name}</p>
                              <p className="text-slate-500">{po.items?.map(i => i.item_name).join(', ')}</p>
                            </div>
                          ))}
                        </div>
                      ) : req.linked_po_number ? (
                        <span className="text-green-600 font-medium">{req.linked_po_number}</span>
                      ) : req.status === 'declined' ? (
                        <span className="text-red-500 text-sm">{req.decline_reason || 'See reason'}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Requisition Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Purchase Requisition</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select 
                  value={formData.department_name} 
                  onValueChange={(v) => setFormData({ ...formData, department_name: v })}
                >
                  <SelectTrigger data-testid="req-department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Kitchen">Kitchen</SelectItem>
                    <SelectItem value="Housekeeping">Housekeeping</SelectItem>
                    <SelectItem value="Front Office">Front Office</SelectItem>
                    <SelectItem value="Food and Beverage">Food and Beverage</SelectItem>
                    <SelectItem value="Maintenance">Maintenance</SelectItem>
                    <SelectItem value="Engineering">Engineering</SelectItem>
                    <SelectItem value="Spa">Spa</SelectItem>
                    <SelectItem value="Security">Security</SelectItem>
                    <SelectItem value="Administration">Administration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Budget Code</Label>
                <Input 
                  value={formData.budget_code}
                  onChange={(e) => setFormData({ ...formData, budget_code: e.target.value })}
                  placeholder="e.g., FOOD-2026"
                  data-testid="req-budget-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Urgency Level</Label>
              <Select 
                value={formData.urgency_level} 
                onValueChange={(v) => setFormData({ ...formData, urgency_level: v })}
              >
                <SelectTrigger data-testid="req-urgency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Items Requested ({formData.items.length} of 20)</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Item Name</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 w-20">Qty</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 w-20">Unit</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 w-28">Est. Price</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {formData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-2 py-2">
                          <Input 
                            value={item.item_name}
                            onChange={(e) => handleItemChange(idx, 'item_name', e.target.value)}
                            placeholder="Item name"
                            className="h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input 
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(idx, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input 
                            value={item.unit}
                            onChange={(e) => handleItemChange(idx, 'unit', e.target.value)}
                            className="h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input 
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.estimated_unit_price}
                            onChange={(e) => handleItemChange(idx, 'estimated_unit_price', parseFloat(e.target.value) || 0)}
                            className="h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRemoveItem(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="flex justify-end">
                <p className="text-sm font-medium text-slate-600">
                  Estimated Total: <span className="text-lg text-[#F59E0B]">${calculateEstimatedTotal().toLocaleString()}</span>
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes or requirements..."
                rows={3}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-[#F59E0B] hover:bg-[#D97706]"
                data-testid="submit-requisition-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                Submit Requisition
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit & Resubmit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit & Resubmit Requisition</DialogTitle>
          </DialogHeader>
          
          {/* Show PM Return Note */}
          {editingReq?.return_reason && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-2" data-testid="edit-return-reason-banner">
              <p className="text-sm font-semibold text-orange-700 mb-1">PM Feedback — Please Address:</p>
              <p className="text-sm text-orange-600">{editingReq.return_reason}</p>
              {editingReq.returned_by_name && (
                <p className="text-xs text-orange-500 mt-2">— {editingReq.returned_by_name}</p>
              )}
            </div>
          )}

          <form onSubmit={handleResubmit} className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select 
                  value={editFormData.department_name} 
                  onValueChange={(v) => setEditFormData({ ...editFormData, department_name: v })}
                >
                  <SelectTrigger data-testid="edit-department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Kitchen">Kitchen</SelectItem>
                    <SelectItem value="Housekeeping">Housekeeping</SelectItem>
                    <SelectItem value="Front Office">Front Office</SelectItem>
                    <SelectItem value="Food and Beverage">Food and Beverage</SelectItem>
                    <SelectItem value="Maintenance">Maintenance</SelectItem>
                    <SelectItem value="Engineering">Engineering</SelectItem>
                    <SelectItem value="Spa">Spa</SelectItem>
                    <SelectItem value="Security">Security</SelectItem>
                    <SelectItem value="Administration">Administration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Budget Code</Label>
                <Input 
                  value={editFormData.budget_code}
                  onChange={(e) => setEditFormData({ ...editFormData, budget_code: e.target.value })}
                  placeholder="e.g., FOOD-2026"
                  data-testid="edit-budget-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Urgency Level</Label>
              <Select 
                value={editFormData.urgency_level} 
                onValueChange={(v) => setEditFormData({ ...editFormData, urgency_level: v })}
              >
                <SelectTrigger data-testid="edit-urgency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Items Requested ({editFormData.items.length} of 20)</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddEditItem}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Item Name</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 w-20">Qty</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 w-20">Unit</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 w-28">Est. Price</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {editFormData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-2 py-2">
                          <Input 
                            value={item.item_name}
                            onChange={(e) => handleEditItemChange(idx, 'item_name', e.target.value)}
                            placeholder="Item name"
                            className="h-8"
                            data-testid={`edit-item-name-${idx}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input 
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleEditItemChange(idx, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-8"
                            data-testid={`edit-item-qty-${idx}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input 
                            value={item.unit}
                            onChange={(e) => handleEditItemChange(idx, 'unit', e.target.value)}
                            className="h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input 
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.estimated_unit_price}
                            onChange={(e) => handleEditItemChange(idx, 'estimated_unit_price', parseFloat(e.target.value) || 0)}
                            className="h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRemoveEditItem(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="flex justify-end">
                <p className="text-sm font-medium text-slate-600">
                  Estimated Total: <span className="text-lg text-[#F59E0B]">${calculateEditTotal().toLocaleString()}</span>
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Any additional notes or requirements..."
                rows={3}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="resubmit-requisition-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                Resubmit Requisition
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyerPortalPage;
